/*
  Datei:         /assets/pandiswelt-theme.js
  Beschreibung:  Modulares JavaScript-Fundament für alle Pandiswelt-Sektionen
                 (Phase 1: Startseite).
  Pattern:       Custom Elements (analog Dawn Theme). Jede Sektion ist
                 self-contained — keine globalen States, kein DOM-Leakage.
  Load:          Sollte mit defer im <head> oder vor </body> geladen werden.
  Dependencies:  keine (Vanilla JS, ES2018+).
*/

(function () {
  'use strict';

  /* ==========================================================================
     1. GLOBALER NAMESPACE & UTILITIES
     ========================================================================== */

  window.Pandiswelt = window.Pandiswelt || {};

  Pandiswelt.utils = {
    /**
     * Debounce — für resize/scroll Listener
     */
    debounce(fn, wait = 150) {
      let timeoutId;
      return function debounced(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    /**
     * Throttle via rAF — für hochfrequente Events
     */
    rafThrottle(fn) {
      let ticking = false;
      return function throttled(...args) {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          fn.apply(this, args);
          ticking = false;
        });
      };
    },

    /**
     * Prüft, ob Nutzer reduzierte Animationen bevorzugt
     */
    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /**
     * Erstellt einen IntersectionObserver mit sinnvollen Defaults
     */
    createScrollObserver(callback, options = {}) {
      const defaults = {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1,
      };
      return new IntersectionObserver(callback, { ...defaults, ...options });
    },

    /**
     * Fokus-Trap innerhalb eines Containers — für Overlays/Modals.
     * Liefert eine cleanup-Funktion.
     */
    trapFocus(container, initialFocusEl = null) {
      const selector =
        'a[href], button:not([disabled]), textarea:not([disabled]),' +
        'input:not([disabled]), select:not([disabled]),' +
        '[tabindex]:not([tabindex="-1"])';
      const focusable = Array.from(container.querySelectorAll(selector));
      if (focusable.length === 0) return () => {};

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      function handleKeydown(event) {
        if (event.key !== 'Tab') return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      container.addEventListener('keydown', handleKeydown);
      (initialFocusEl || first).focus();

      return () => container.removeEventListener('keydown', handleKeydown);
    },

    /**
     * Body-Scroll-Lock / Unlock — für geöffnete Overlays.
     */
    lockScroll() {
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('pandiswelt-scroll-locked');
      document.body.dataset.pandisweltScrollY = String(scrollY);
    },

    unlockScroll() {
      const scrollY = parseInt(document.body.dataset.pandisweltScrollY || '0', 10);
      document.body.classList.remove('pandiswelt-scroll-locked');
      document.body.style.top = '';
      delete document.body.dataset.pandisweltScrollY;
      window.scrollTo(0, scrollY);
    },

    /**
     * localStorage Safe-Wrapper (Safari Private Mode wirft sonst)
     */
    storage: {
      get(key) {
        try {
          const raw = window.localStorage.getItem(key);
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      },
      set(key, value) {
        try {
          window.localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch {
          return false;
        }
      },
      remove(key) {
        try {
          window.localStorage.removeItem(key);
          return true;
        } catch {
          return false;
        }
      },
    },

    /**
     * Shopify AJAX Cart API — Produkt hinzufügen
     */
    async cartAdd(variantId, quantity = 1, properties = null) {
      const body = { id: variantId, quantity };
      if (properties) body.properties = properties;

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Cart add failed');
      }
      return response.json();
    },

    /**
     * Shopify AJAX Cart API — aktuellen Warenkorb abfragen
     */
    async cartGet() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json();
    },

    /**
     * Cart-Aktualisierung broadcasten — andere Komponenten (Header-Count etc.)
     * können darauf hören via: document.addEventListener('pandiswelt:cart:updated', ...)
     */
    dispatchCartUpdated(cart) {
      document.dispatchEvent(
        new CustomEvent('pandiswelt:cart:updated', { detail: { cart } })
      );
    },
  };


  /* ==========================================================================
     2. CUSTOM ELEMENTS — SEKTIONEN
     Skeletons werden inkrementell mit jeder Sektion der Phase 1 implementiert.
     Jede Sektion registriert sich selbst via customElements.define().
     ========================================================================== */

  /* --- SEKTION 1: STORY BUBBLES ---------------------------------------
     Eigenständiges Custom Element. Pro Sektion eine Instanz.
     - Overlay-Handling (öffnen/schließen, Escape, Klick außerhalb)
     - Fokus-Trap im geöffneten Overlay, Fokus-Rückgabe beim Schließen
     - Body-Scroll-Lock
     - Gesehen-State pro Block-ID in localStorage (ein Key pro Section)
     - Lazy Content: Inhalte werden erst beim ersten Öffnen aus dem <template> geklont
  */
  class PandisweltStoryBubbles extends HTMLElement {
    constructor() {
      super();
      this.sectionId       = this.dataset.sectionId || 'default';
      this.storageKey      = `pandiswelt:bubbles:seen:${this.sectionId}`;
      this.triggers        = [];
      this.overlay         = null;
      this.contentTarget   = null;
      this.closeBtn        = null;
      this.lastFocused     = null;
      this.releaseFocusTrap = null;
      this.isOpen          = false;

      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onCloseClick   = this._onCloseClick.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onKeydown      = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.triggers      = Array.from(this.querySelectorAll('[data-bubble-trigger]'));
      this.overlay       = this.querySelector('[data-bubble-overlay]');
      this.contentTarget = this.querySelector('[data-bubble-content-target]');
      this.closeBtn      = this.querySelector('[data-bubble-close]');

      if (!this.overlay || !this.contentTarget) return;

      this._applySeenStates();

      this.triggers.forEach((btn) => btn.addEventListener('click', this._onTriggerClick));
      this.closeBtn && this.closeBtn.addEventListener('click', this._onCloseClick);
      this.overlay.addEventListener('click', this._onOverlayClick);
    }

    disconnectedCallback() {
      this.triggers.forEach((btn) => btn.removeEventListener('click', this._onTriggerClick));
      this.closeBtn && this.closeBtn.removeEventListener('click', this._onCloseClick);
      this.overlay && this.overlay.removeEventListener('click', this._onOverlayClick);
      document.removeEventListener('keydown', this._onKeydown);
      if (this.isOpen) this._close(true);
    }

    /* ---- Gesehen-Tracking ---------------------------------------- */

    _getSeen() {
      const stored = Pandiswelt.utils.storage.get(this.storageKey);
      return Array.isArray(stored) ? new Set(stored) : new Set();
    }

    _markSeen(blockId) {
      /* DEAKTIVIERT seit User-Wunsch — Ring soll immer farbig bleiben.
         Falls Instagram-Style spaeter doch gewuenscht: original-Code unten
         einkommentieren, dann markiert er wieder in localStorage. */
      /*
      const set = this._getSeen();
      if (set.has(blockId)) return;
      set.add(blockId);
      Pandiswelt.utils.storage.set(this.storageKey, Array.from(set));
      */
    }

    _applySeenStates() {
      const seen = this._getSeen();
      this.triggers.forEach((btn) => {
        if (seen.has(btn.dataset.blockId)) {
          btn.setAttribute('data-seen', 'true');
        }
      });
    }

    /* ---- Open / Close ------------------------------------------- */

    _onTriggerClick(event) {
      const btn = event.currentTarget;
      const blockId = btn.dataset.blockId;
      if (!blockId) return;

      const template = this.querySelector(`template[data-bubble-content="${blockId}"]`);
      if (!template) return;

      /* Inhalt (erneut) in die Overlay-DOM schreiben */
      this.contentTarget.replaceChildren(template.content.cloneNode(true));

      this.lastFocused = btn;
      this._open();

      /* DEAKTIVIERT: "Gesehen"-Markierung. Ring bleibt immer farbig. */
      /* this._markSeen(blockId);
         btn.setAttribute('data-seen', 'true'); */
    }

    _open() {
      if (this.isOpen) return;
      this.isOpen = true;

      this.overlay.classList.add('pandiswelt-overlay--open');
      this.overlay.setAttribute('aria-hidden', 'false');

      Pandiswelt.utils.lockScroll();
      document.addEventListener('keydown', this._onKeydown);

      /* Fokus-Trap im nächsten Frame, damit CSS-Transition sauber startet */
      requestAnimationFrame(() => {
        this.releaseFocusTrap = Pandiswelt.utils.trapFocus(this.overlay, this.closeBtn);
      });
    }

    _close(silent = false) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.overlay.classList.remove('pandiswelt-overlay--open');
      this.overlay.setAttribute('aria-hidden', 'true');

      document.removeEventListener('keydown', this._onKeydown);
      if (typeof this.releaseFocusTrap === 'function') {
        this.releaseFocusTrap();
        this.releaseFocusTrap = null;
      }
      Pandiswelt.utils.unlockScroll();

      /* Overlay-Inhalt nach Ende der CSS-Transition leeren
         (vermeidet Aufblitzen beim nächsten Öffnen) */
      if (!silent) {
        window.setTimeout(() => {
          if (!this.isOpen) this.contentTarget.replaceChildren();
        }, 300);
      } else {
        this.contentTarget.replaceChildren();
      }

      /* Fokus zurück auf die ursprüngliche Bubble */
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
      this.lastFocused = null;
    }

    _onCloseClick() {
      this._close();
    }

    _onOverlayClick(event) {
      /* Nur schließen, wenn direkt auf das Backdrop geklickt wurde,
         nicht auf Kind-Elemente (Content, Close-Button etc.). */
      if (event.target === this.overlay) this._close();
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this._close();
      }
    }
  }

  if (!customElements.get('pandiswelt-story-bubbles')) {
    customElements.define('pandiswelt-story-bubbles', PandisweltStoryBubbles);
  }

  /* --- SEKTION 2: HERO BANNER -----------------------------------------
     Zweck: Hintergrund-Video erst NACH window.load aktivieren,
     damit der Download nicht mit dem LCP-Bild konkurriert.
     Zusätzlich: Video pausieren, wenn Hero außerhalb des Viewports.
     Prefers-reduced-motion: Video wird gar nicht geladen.
  */
  class PandisweltHeroBanner extends HTMLElement {
    constructor() {
      super();
      this.video           = null;
      this.ioVisibility    = null;
      this.isInViewport    = true;
      this.videoInitialized = false;
      this._onVideoReady   = this._onVideoReady.bind(this);
      this._onVideoError   = this._onVideoError.bind(this);
    }

    connectedCallback() {
      if (this.dataset.hasVideo !== 'true') return;

      this.video = this.querySelector('[data-hero-video]');
      if (!this.video || !this.video.dataset.src) return;

      /* Reduced-Motion respektieren */
      if (Pandiswelt.utils.prefersReducedMotion()) {
        this.video.remove();
        this.video = null;
        return;
      }

      /* Nach window.load laden — oder sofort, falls schon fertig */
      if (document.readyState === 'complete') {
        this._initVideo();
      } else {
        window.addEventListener('load', () => this._initVideo(), { once: true });
      }
    }

    disconnectedCallback() {
      if (this.ioVisibility) {
        this.ioVisibility.disconnect();
        this.ioVisibility = null;
      }
      if (this.video) {
        this.video.removeEventListener('loadeddata', this._onVideoReady);
        this.video.removeEventListener('error', this._onVideoError);
        /* Browser sauber aufräumen lassen */
        try { this.video.pause(); } catch (_) { /* ignore */ }
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    _initVideo() {
      if (this.videoInitialized || !this.video) return;
      this.videoInitialized = true;

      this.video.addEventListener('loadeddata', this._onVideoReady, { once: true });
      this.video.addEventListener('error', this._onVideoError, { once: true });

      /* Src setzen → Browser lädt */
      this.video.src = this.video.dataset.src;
      this.video.load();

      this._observeVisibility();
    }

    _onVideoReady() {
      if (!this.video) return;
      this.video.classList.add('is-ready');
      this._tryPlay();
    }

    _onVideoError() {
      /* Bei Fehler: Video-Element entfernen, Poster-Bild bleibt sichtbar */
      if (this.video) {
        this.video.remove();
        this.video = null;
      }
    }

    _tryPlay() {
      if (!this.video || !this.isInViewport) return;
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        /* Autoplay wurde vom Browser blockiert — Poster bleibt einfach als Standbild. */
        playPromise.catch(() => { /* no-op */ });
      }
    }

    _observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.ioVisibility = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isInViewport = entry.isIntersecting;
          if (!this.video) return;
          if (entry.isIntersecting) {
            this._tryPlay();
          } else {
            try { this.video.pause(); } catch (_) { /* ignore */ }
          }
        });
      }, { threshold: 0.1 });

      this.ioVisibility.observe(this);
    }
  }

  if (!customElements.get('pandiswelt-hero-banner')) {
    customElements.define('pandiswelt-hero-banner', PandisweltHeroBanner);
  }

  /* --- SEKTION 3: USP LEISTE ------------------------------------------
     Auto-Carousel nur unter folgenden Bedingungen:
       - mobile_display === 'carousel'
       - block_count > 2  (also ab 3 USPs)
       - Viewport < 750px (reagiert auf Resize)
       - nicht prefers-reduced-motion
     Carousel pausiert bei Hover/Focus, rotiert alle 4.5s.
  */
  class PandisweltUspBar extends HTMLElement {
    constructor() {
      super();
      this.items        = [];
      this.dots         = [];
      this.currentIndex = 0;
      this.intervalId   = null;
      this.isPaused     = false;
      this.mq           = window.matchMedia('(max-width: 749px)');
      this.isEnabled    = false;

      this._pause        = this._pause.bind(this);
      this._resume       = this._resume.bind(this);
      this._onFocusOut   = this._onFocusOut.bind(this);
      this._onDotClick   = this._onDotClick.bind(this);
      this._onMqChange   = this._onMqChange.bind(this);
    }

    connectedCallback() {
      if (this.dataset.mobileDisplay !== 'carousel') return;

      const count = parseInt(this.dataset.blockCount || '0', 10);
      if (count <= 2) return;

      if (Pandiswelt.utils.prefersReducedMotion()) return;

      this.items = Array.from(this.querySelectorAll('[data-usp-item]'));
      this.dots  = Array.from(this.querySelectorAll('[data-usp-dot]'));
      if (this.items.length === 0) return;

      this.dots.forEach((dot) => dot.addEventListener('click', this._onDotClick));

      /* matchMedia-Browser-Kompatibilität: addEventListener seit Safari 14 */
      if (typeof this.mq.addEventListener === 'function') {
        this.mq.addEventListener('change', this._onMqChange);
      } else if (typeof this.mq.addListener === 'function') {
        this.mq.addListener(this._onMqChange);
      }

      if (this.mq.matches) this._enable();
    }

    disconnectedCallback() {
      this._disable();
      this.dots.forEach((dot) => dot.removeEventListener('click', this._onDotClick));
      if (typeof this.mq.removeEventListener === 'function') {
        this.mq.removeEventListener('change', this._onMqChange);
      } else if (typeof this.mq.removeListener === 'function') {
        this.mq.removeListener(this._onMqChange);
      }
    }

    _onMqChange(event) {
      if (event.matches) this._enable();
      else               this._disable();
    }

    _enable() {
      if (this.isEnabled) return;
      this.isEnabled = true;

      this.classList.add('is-carousel-active');
      this._show(0);

      this.addEventListener('pointerenter', this._pause);
      this.addEventListener('pointerleave', this._resume);
      this.addEventListener('focusin',      this._pause);
      this.addEventListener('focusout',     this._onFocusOut);

      this._startCycle();
    }

    _disable() {
      if (!this.isEnabled) return;
      this.isEnabled = false;

      this.classList.remove('is-carousel-active');
      this._stopCycle();

      this.items.forEach((item) => item.classList.remove('is-active'));
      this.dots.forEach((dot)   => dot.classList.remove('is-active'));

      this.removeEventListener('pointerenter', this._pause);
      this.removeEventListener('pointerleave', this._resume);
      this.removeEventListener('focusin',      this._pause);
      this.removeEventListener('focusout',     this._onFocusOut);
    }

    _startCycle() {
      this._stopCycle();
      this.intervalId = window.setInterval(() => {
        if (this.isPaused) return;
        const next = (this.currentIndex + 1) % this.items.length;
        this._show(next);
      }, 4500);
    }

    _stopCycle() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    _show(index) {
      this.currentIndex = index;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      this.dots.forEach((dot,   i) => dot.classList.toggle('is-active',  i === index));
    }

    _pause()  { this.isPaused = true; }
    _resume() { this.isPaused = false; }

    _onFocusOut(event) {
      /* Nur wirklich "resumen", wenn der Fokus das Element komplett verlässt */
      if (!this.contains(event.relatedTarget)) this.isPaused = false;
    }

    _onDotClick(event) {
      const index = parseInt(event.currentTarget.dataset.uspDot, 10);
      if (Number.isNaN(index)) return;
      this._show(index);
      this._startCycle(); /* Timer neu starten */
    }
  }

  if (!customElements.get('pandiswelt-usp-bar')) {
    customElements.define('pandiswelt-usp-bar', PandisweltUspBar);
  }

  /* --- SEKTION 4: KATEGORIE KACHELN -----------------------------------
     Kein JS benötigt — Hover-Zoom, Grid und Label-Modi sind rein CSS.
  */

  /* --- SEKTION 5: WIE FUNKTIONIERT ES ---------------------------------
     Kein eigenes Custom Element nötig. Scroll-Animation läuft über den
     globalen Observer (.pandiswelt-animate-in), siehe initScrollAnimations
     weiter unten.
  */

  /* --- SEKTION 6: PRODUKT-GRID ----------------------------------------
     - Tabs: Klick wechselt aktives Panel, ARIA-States werden gepflegt
     - Tastatur-Navigation: ArrowLeft/Right wechselt Tab, Home/End springt
     - Quick-Add: nutzt Pandiswelt.utils.cartAdd, zeigt Erfolg auf Button
       und broadcastet pandiswelt:cart:updated für Header-Cart-Counter
  */
  class PandisweltProductGrid extends HTMLElement {
    constructor() {
      super();
      this.tabs    = [];
      this.panels  = [];

      this._onTabClick    = this._onTabClick.bind(this);
      this._onTabKeydown  = this._onTabKeydown.bind(this);
      this._onQuickAdd    = this._onQuickAdd.bind(this);
    }

    connectedCallback() {
      this._setupTabs();
      this._setupQuickAdd();
    }

    disconnectedCallback() {
      this.tabs.forEach((t) => {
        t.removeEventListener('click',    this._onTabClick);
        t.removeEventListener('keydown',  this._onTabKeydown);
      });
      this.querySelectorAll('[data-pg-quickadd]').forEach((b) => {
        b.removeEventListener('click', this._onQuickAdd);
      });
    }

    /* ---------- Tabs ---------- */

    _setupTabs() {
      this.tabs = Array.from(this.querySelectorAll('[data-pg-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-pg-panel]'));
      if (this.tabs.length === 0) return;

      this.tabs.forEach((t) => {
        t.addEventListener('click',   this._onTabClick);
        t.addEventListener('keydown', this._onTabKeydown);
      });
    }

    _onTabClick(event) {
      const idx = parseInt(event.currentTarget.dataset.pgTab, 10);
      if (Number.isNaN(idx)) return;
      this._activateTab(idx);
    }

    _onTabKeydown(event) {
      const currentIdx = this.tabs.indexOf(event.currentTarget);
      if (currentIdx < 0) return;

      let nextIdx = -1;
      switch (event.key) {
        case 'ArrowLeft':  nextIdx = (currentIdx - 1 + this.tabs.length) % this.tabs.length; break;
        case 'ArrowRight': nextIdx = (currentIdx + 1) % this.tabs.length; break;
        case 'Home':       nextIdx = 0; break;
        case 'End':        nextIdx = this.tabs.length - 1; break;
        default: return;
      }

      event.preventDefault();
      this._activateTab(nextIdx);
      this.tabs[nextIdx].focus();
    }

    _activateTab(activeIdx) {
      this.tabs.forEach((tab, i) => {
        const isActive = i === activeIdx;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      this.panels.forEach((panel, i) => {
        const isActive = i === activeIdx;
        panel.classList.toggle('is-active', isActive);
        if (isActive) panel.removeAttribute('hidden');
        else          panel.setAttribute('hidden', '');
      });
    }

    /* ---------- Quick-Add ---------- */

    _setupQuickAdd() {
      this.querySelectorAll('[data-pg-quickadd]').forEach((btn) => {
        btn.addEventListener('click', this._onQuickAdd);
      });
    }

    async _onQuickAdd(event) {
      const btn = event.currentTarget;
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.disabled) return;

      btn.disabled = true;
      btn.classList.add('is-loading');

      try {
        await Pandiswelt.utils.cartAdd(variantId, 1);
        const cart = await Pandiswelt.utils.cartGet();
        Pandiswelt.utils.dispatchCartUpdated(cart);

        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        const labelEl = btn.querySelector('[data-pg-quickadd-label]');
        const originalLabel = labelEl ? labelEl.textContent : '';
        if (labelEl) labelEl.textContent = 'Hinzugefügt';

        window.setTimeout(() => {
          btn.classList.remove('is-success');
          if (labelEl) labelEl.textContent = originalLabel;
          btn.disabled = false;
        }, 1500);
      } catch (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        /* eslint-disable-next-line no-console */
        console.error('Pandiswelt Quick-Add Fehler:', err);
      }
    }
  }

  if (!customElements.get('pandiswelt-product-grid')) {
    customElements.define('pandiswelt-product-grid', PandisweltProductGrid);
  }

  /* --- SEKTION 7: ÜBER UNS --------------------------------------------
     Kein eigenes Custom Element. Scroll-Animation läuft über den
     globalen Observer (.pandiswelt-animate-in).
  */

  /* --- SEKTION 8: KUNDENBEWERTUNGEN -----------------------------------
     Im Slider-Modus: Prev/Next-Pfeile scrollen die Liste, ScrollSnap macht
     den Rest. Pfeile werden disabled wenn am Anfang/Ende.
     Im Grid-Modus: kein JS aktiv (außer der Klasse, die ohnehin gerendert wird).
  */
  class PandisweltReviews extends HTMLElement {
    constructor() {
      super();
      this.list   = null;
      this.btnPrev = null;
      this.btnNext = null;
      this.isSlider = false;

      this._onPrev   = this._onPrev.bind(this);
      this._onNext   = this._onNext.bind(this);
      this._onScroll = this._onScroll.bind(this);
    }

    connectedCallback() {
      this.isSlider = this.classList.contains('pandiswelt-reviews--layout-slider');
      if (!this.isSlider) return;

      this.list    = this.querySelector('[data-reviews-list]');
      this.btnPrev = this.querySelector('[data-reviews-prev]');
      this.btnNext = this.querySelector('[data-reviews-next]');
      if (!this.list) return;

      this.btnPrev && this.btnPrev.addEventListener('click', this._onPrev);
      this.btnNext && this.btnNext.addEventListener('click', this._onNext);
      this.list.addEventListener('scroll', this._onScroll, { passive: true });

      /* Initial-State der Pfeile setzen */
      this._updateNavState();

      /* Bei Resize evtl. neu evaluieren (Karten-Breite kann sich ändern) */
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this._updateNavState());
        this._resizeObserver.observe(this.list);
      }
    }

    disconnectedCallback() {
      this.btnPrev && this.btnPrev.removeEventListener('click', this._onPrev);
      this.btnNext && this.btnNext.removeEventListener('click', this._onNext);
      this.list && this.list.removeEventListener('scroll', this._onScroll);
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    _getStep() {
      const firstItem = this.list.querySelector('.pandiswelt-reviews__item');
      if (!firstItem) return this.list.clientWidth;
      const styles = window.getComputedStyle(this.list);
      const gap = parseInt(styles.columnGap || styles.gap || '0', 10) || 0;
      return firstItem.offsetWidth + gap;
    }

    _onPrev() {
      this.list.scrollBy({ left: -this._getStep(), behavior: 'smooth' });
    }

    _onNext() {
      this.list.scrollBy({ left:  this._getStep(), behavior: 'smooth' });
    }

    _onScroll() {
      /* Throttle via rAF, vermeidet Layout-Thrashing bei vielen Scroll-Events */
      if (this._scrollTicking) return;
      this._scrollTicking = true;
      requestAnimationFrame(() => {
        this._updateNavState();
        this._scrollTicking = false;
      });
    }

    _updateNavState() {
      if (!this.list) return;
      const atStart = this.list.scrollLeft <= 4;
      const atEnd   = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 4;
      if (this.btnPrev) this.btnPrev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      if (this.btnNext) this.btnNext.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }

  if (!customElements.get('pandiswelt-reviews')) {
    customElements.define('pandiswelt-reviews', PandisweltReviews);
  }

  /* --- SEKTION 9: NEWSLETTER ------------------------------------------
     - Native Browser-Validation unterdruecken (novalidate)
     - Eigene Validation mit hubschem Tooltip-Hinweis
     - is-loading auf Submit-Button beim Absenden
     - Scroll zur Erfolgsmeldung wenn vorhanden
     - BFCache-Restore (Button-Reset bei Browser-Back)
  */
  class PandisweltNewsletter extends HTMLElement {
    constructor() {
      super();
      this.form        = null;
      this.field       = null;
      this.input       = null;
      this.submit      = null;
      this.tooltip     = null;
      this.tooltipText = null;
      this.message     = null;
      this.hideTooltipTimer = null;

      this._onSubmit   = this._onSubmit.bind(this);
      this._onInput    = this._onInput.bind(this);
      this._onBlur     = this._onBlur.bind(this);
      this._onPageShow = this._onPageShow.bind(this);
    }

    connectedCallback() {
      this.form        = this.querySelector('form');
      this.field       = this.querySelector('[data-nl-field]');
      this.input       = this.querySelector('[data-nl-input]');
      this.submit      = this.querySelector('[data-nl-submit]');
      this.tooltip     = this.querySelector('[data-nl-tooltip]');
      this.tooltipText = this.querySelector('[data-nl-tooltip-text]');
      this.message     = this.querySelector('.pandiswelt-nl__message--success');

      if (this.form) {
        /* novalidate-Attribut sicher setzen, falls Liquid es nicht durchgereicht hat */
        this.form.setAttribute('novalidate', 'novalidate');
        this.form.addEventListener('submit', this._onSubmit);
      }
      if (this.input) {
        this.input.addEventListener('input', this._onInput);
        this.input.addEventListener('blur', this._onBlur);
      }
      window.addEventListener('pageshow', this._onPageShow);

      /* Erfolgsmeldung sanft anvisieren */
      if (this.message) {
        requestAnimationFrame(() => {
          this.message.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }

    disconnectedCallback() {
      if (this.form)  this.form.removeEventListener('submit', this._onSubmit);
      if (this.input) {
        this.input.removeEventListener('input', this._onInput);
        this.input.removeEventListener('blur', this._onBlur);
      }
      window.removeEventListener('pageshow', this._onPageShow);
      if (this.hideTooltipTimer) clearTimeout(this.hideTooltipTimer);
    }

    /* ---- Validation ---- */

    _validate() {
      if (!this.input) return { valid: true };
      const value = this.input.value.trim();

      if (value === '') {
        return {
          valid: false,
          message: this.field.dataset.msgEmpty || 'Bitte gib deine E-Mail-Adresse ein.'
        };
      }

      /* RFC-5322-light: ein @, mindestens ein Punkt nach @, keine Spaces */
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return {
          valid: false,
          message: this.field.dataset.msgInvalid || 'Bitte gib eine gueltige E-Mail-Adresse ein.'
        };
      }

      return { valid: true };
    }

    _showTooltip(message) {
      if (!this.tooltip || !this.tooltipText) return;
      this.tooltipText.textContent = message;
      this.tooltip.hidden = false;
      if (this.field) this.field.classList.add('is-invalid');
      if (this.input) this.input.setAttribute('aria-invalid', 'true');

      /* Auto-Hide nach 5 Sekunden */
      if (this.hideTooltipTimer) clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = setTimeout(() => this._hideTooltip(), 5000);
    }

    _hideTooltip() {
      if (!this.tooltip) return;
      this.tooltip.hidden = true;
      if (this.field) this.field.classList.remove('is-invalid');
      if (this.input) this.input.removeAttribute('aria-invalid');
      if (this.hideTooltipTimer) {
        clearTimeout(this.hideTooltipTimer);
        this.hideTooltipTimer = null;
      }
    }

    /* ---- Events ---- */

    _onSubmit(event) {
      /* Defensive: Wenn Tooltip-DOM fehlt, lass den Browser machen, was er will,
         damit der User nicht festhaengt. */
      if (!this.input || !this.field || !this.tooltip) {
        console.warn('[Pandiswelt Newsletter] Tooltip-DOM nicht gefunden, ueberspringe Validation.');
        return;
      }

      const result = this._validate();
      if (!result.valid) {
        event.preventDefault();
        this._showTooltip(result.message);
        if (this.input) this.input.focus();
        return;
      }

      /* Valide — Loading-State setzen, Form an Shopify abschicken */
      this._hideTooltip();
      if (this.submit) {
        this.submit.classList.add('is-loading');
        this.submit.disabled = true;
      }
    }

    _onInput() {
      /* Tooltip ausblenden, sobald User tippt */
      if (this.tooltip && !this.tooltip.hidden) {
        this._hideTooltip();
      }
    }

    _onBlur() {
      /* Bei Verlassen des Feldes nicht direkt validieren — nur beim Submit.
         User soll ohne stoerende Bubbles tippen koennen. */
    }

    _onPageShow(event) {
      if (event.persisted && this.submit) {
        this.submit.classList.remove('is-loading');
        this.submit.disabled = false;
      }
    }
  }

  if (!customElements.get('pandiswelt-newsletter')) {
    customElements.define('pandiswelt-newsletter', PandisweltNewsletter);
  }


  /* --- SEKTION 10: ANKUENDIGUNGSLEISTE ---------------------------------
     Aktiviert nur dann JS-Logik, wenn:
       mode === 'static' && sub_mode === 'slideshow'
     In Marquee-Modus läuft die Animation pure CSS.
     Im static+separator-Modus passiert hier gar nichts.
     Slideshow pausiert bei prefers-reduced-motion.
  */
  class PandisweltAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slides       = [];
      this.currentIndex = 0;
      this.intervalId   = null;
    }

    connectedCallback() {
      const mode    = this.dataset.mode;
      const subMode = this.dataset.subMode;

      if (mode !== 'static' || subMode !== 'slideshow') return;
      if (Pandiswelt.utils.prefersReducedMotion()) return;

      this.slides = Array.from(this.querySelectorAll('[data-ab-slide]'));
      if (this.slides.length <= 1) return;

      const intervalSec = parseInt(this.dataset.slideshowInterval, 10) || 4;
      const ms = intervalSec * 1000;

      this.intervalId = window.setInterval(() => this._next(), ms);
    }

    disconnectedCallback() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    _next() {
      const prevIndex = this.currentIndex;
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.slides[prevIndex].classList.remove('is-active');
      this.slides[this.currentIndex].classList.add('is-active');
    }
  }

  if (!customElements.get('pandiswelt-announcement-bar')) {
    customElements.define('pandiswelt-announcement-bar', PandisweltAnnouncementBar);
  }


  /* ==========================================================================
     3. GLOBALE SCROLL-ANIMATIONS-INITIALISIERUNG
     Scannt nach .pandiswelt-animate-in und triggert die CSS-Transition
     beim Viewport-Eintritt. Elemente werden nur einmal animiert.
     ========================================================================== */

  function initScrollAnimations(root = document) {
    const elements = root.querySelectorAll('.pandiswelt-animate-in:not(.is-visible)');
    if (elements.length === 0) return;

    if (Pandiswelt.utils.prefersReducedMotion() || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = Pandiswelt.utils.createScrollObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    });

    elements.forEach((el) => observer.observe(el));
  }

  Pandiswelt.initScrollAnimations = initScrollAnimations;


  /* ==========================================================================
     4. BOOTSTRAPPING
     ========================================================================== */

  function bootstrap() {
    initScrollAnimations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /* Re-init bei Theme-Editor Section-Load — damit neu hinzugefügte
     Sektionen im Customizer sofort funktionieren. */
  document.addEventListener('shopify:section:load', (event) => {
    initScrollAnimations(event.target);
  });

  /* Overlay-Cleanup, falls eine Sektion mit offenem Overlay entfernt wird */
  document.addEventListener('shopify:section:unload', () => {
    if (document.body.classList.contains('pandiswelt-scroll-locked')) {
      Pandiswelt.utils.unlockScroll();
    }
  });
})();
